import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Loading, PageHeader } from '../App';
import {
  adminApi,
  dateTime,
  errorMessage,
  money,
  type PaymentConfiguration,
  type PaymentConfigurationCategory,
  type PaymentConfigurationWrite,
} from '../api/admin';

/**
 * Super Admin control over what the platform charges for, and what happens when a
 * payment lapses.
 *
 * Four independent charges. Nothing here is a price list held in the browser: every
 * amount, duration and grace period is read from the API, and every save is re-validated
 * on the server before it is accepted.
 */

interface Section {
  category: PaymentConfigurationCategory;
  title: string;
  blurb: string;
  periodLabel: string;
  periodHelp: string;
  amountLabel: string;
  validityLabel: string;
  validityHelp: string;
  graceHelp: string;
  countLabel: string;
  /** Only the organisation entitlement can be withdrawn as a capability. */
  canDisableFeature?: boolean;
}

const SECTIONS: Section[] = [
  {
    category: 'HostelSubscription',
    title: 'PG / Hostel monthly subscription',
    blurb: 'Charged per branch. Each branch subscribes on its own and expires on its own.',
    periodLabel: 'Billing period',
    periodHelp: 'Monthly branch subscription.',
    amountLabel: 'Monthly amount',
    validityLabel: 'Duration (days)',
    validityHelp: 'How long one payment lasts.',
    graceHelp: 'Days a lapsed branch stays visible to customers.',
    countLabel: 'branches',
  },
  {
    category: 'HotelSubscription',
    title: 'Hotel monthly subscription',
    blurb: 'Charged per hotel branch, on the same per-branch basis as PG and hostels.',
    periodLabel: 'Billing period',
    periodHelp: 'Monthly branch subscription.',
    amountLabel: 'Monthly amount',
    validityLabel: 'Duration (days)',
    validityHelp: 'How long one payment lasts.',
    graceHelp: 'Days a lapsed branch stays visible to customers.',
    countLabel: 'branches',
  },
  {
    category: 'MultiBranchSubscription',
    title: 'Yearly multi-branch subscription',
    blurb:
      'Charged once per organisation, not per branch. It grants the right to run more than '
      + 'one branch; each branch still carries its own monthly subscription.',
    periodLabel: 'Billing basis',
    periodHelp: 'Yearly, per organisation.',
    amountLabel: 'Yearly amount',
    validityLabel: 'Duration (days)',
    validityHelp: 'How long one payment lasts. 365 for a year.',
    graceHelp: 'Days after expiry before every branch of the organisation is hidden.',
    countLabel: 'organisations',
    canDisableFeature: true,
  },
  {
    category: 'HomeListingFee',
    title: 'Home rental listing fee',
    blurb: 'Charged once per listing. Paying for one home never publishes another.',
    periodLabel: 'Billing basis',
    periodHelp: 'One-time fee per listing.',
    amountLabel: 'Listing fee',
    validityLabel: 'Listing validity (days)',
    validityHelp: 'How long a paid listing stays published.',
    graceHelp: 'Normally zero: a listing is a one-time posting, not a running service.',
    countLabel: 'listings',
  },
];

export function PaymentConfigurationPage() {
  const [configs, setConfigs] = useState<PaymentConfiguration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setConfigs(await adminApi.paymentConfiguration());
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSaved = (saved: PaymentConfiguration) => {
    setConfigs((current) => (current ?? []).map((c) => (c.category === saved.category ? saved : c)));
    setSuccess(describeSave(saved));
    setError(null);
  };

  return (
    <>
      <PageHeader
        title="Payment configuration"
        description="What the platform charges for, and what happens when a payment lapses. Changes take effect on the next submission or renewal; nothing already sold is repriced."
      />

      <Alert message={error} />
      <Alert message={success} variant="success" />

      {!configs ? (
        <Loading rows={8} />
      ) : (
        SECTIONS.map((section) => {
          const config = configs.find((c) => c.category === section.category);
          if (!config) return null;

          return (
            <CategoryForm
              key={section.category}
              section={section}
              config={config}
              onSaved={onSaved}
              onError={(message) => {
                setError(message);
                setSuccess(null);
              }}
            />
          );
        })
      )}

      <div className="admin-card">
        <h2 className="h6">How these interact</h2>
        <ul className="small text-muted mb-0">
          <li>
            A branch is shown to customers only when <strong>both</strong> rules pass: its own monthly subscription is
            live or in grace, <strong>and</strong> — where its organisation runs more than one branch — the yearly
            subscription is live or in grace.
          </li>
          <li>
            Letting the yearly subscription lapse past its grace hides <strong>every</strong> branch of that
            organisation, including branches whose own monthly subscription is paid up. A single-branch organisation is
            never affected, because it needs no yearly subscription.
          </li>
          <li>
            Switching <strong>multi-branch off</strong> stops owners adding further branches. Existing branches keep
            working on their own monthly subscriptions.
          </li>
          <li>
            Changing an amount, tax rate or duration creates a new version of the managed plan. Subscriptions already
            sold hold their own amount and expiry, so none is repriced.
          </li>
          <li>
            Grace periods are recorded when a subscription is sold. Shortening one here does not retroactively hide
            anything already inside its window.
          </li>
          <li>Free and administratively granted entitlements are never counted as revenue.</li>
        </ul>
      </div>
    </>
  );
}

function describeSave(saved: PaymentConfiguration): string {
  const name = SECTIONS.find((s) => s.category === saved.category)?.title ?? saved.category;

  if (saved.category === 'MultiBranchSubscription' && !saved.featureEnabled) {
    return `${name} is switched off. Owners cannot add further branches; existing branches are unaffected.`;
  }

  return saved.paymentEnabled
    ? `${name} now requires ${money(saved.totalAmount)} per ${saved.listingValidityDays} days, with a ${saved.gracePeriodDays}-day grace period.`
    : `${name} no longer requires payment.`;
}

function CategoryForm({
  section,
  config,
  onSaved,
  onError,
}: {
  section: Section;
  config: PaymentConfiguration;
  onSaved: (saved: PaymentConfiguration) => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState<PaymentConfigurationWrite>(toDraft(config));
  const [saving, setSaving] = useState(false);

  // Reset whenever the server hands back a newer version of this row.
  useEffect(() => {
    setDraft(toDraft(config));
  }, [config]);

  const changed =
    draft.paymentEnabled !== config.paymentEnabled ||
    draft.featureEnabled !== config.featureEnabled ||
    Number(draft.amount) !== config.amount ||
    Number(draft.taxPercentage) !== config.taxPercentage ||
    Number(draft.listingValidityDays) !== config.listingValidityDays ||
    Number(draft.gracePeriodDays) !== config.gracePeriodDays ||
    draft.currency !== config.currency;

  const featureOff = section.canDisableFeature === true && !draft.featureEnabled;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);

    try {
      onSaved(
        await adminApi.savePaymentConfiguration(config.category, {
          ...draft,
          amount: Number(draft.amount),
          taxPercentage: Number(draft.taxPercentage),
          listingValidityDays: Number(draft.listingValidityDays),
          gracePeriodDays: Number(draft.gracePeriodDays),
        }),
      );
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const id = (field: string) => `${config.category}-${field}`;

  return (
    <form className="admin-card mb-4" onSubmit={submit} noValidate>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
        <div>
          <h2 className="h6 mb-1">{section.title}</h2>
          <p className="small text-muted mb-0">{section.blurb}</p>
        </div>

        <span className={`badge bg-${badgeTone(config, section)}`}>{badgeLabel(config, section)}</span>
      </div>

      {section.canDisableFeature && (
        <div className="form-check form-switch mt-3 mb-1">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={id('feature')}
            checked={draft.featureEnabled}
            onChange={(e) => setDraft({ ...draft, featureEnabled: e.target.checked })}
          />
          <label className="form-check-label" htmlFor={id('feature')}>
            Multi-branch functionality enabled
          </label>
          <div className="form-text">
            Off means no organisation can add a further branch, paid or not. Branches that already exist keep working.
          </div>
        </div>
      )}

      <div className="form-check form-switch my-3">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id={id('enabled')}
          checked={draft.paymentEnabled}
          disabled={featureOff}
          onChange={(e) => setDraft({ ...draft, paymentEnabled: e.target.checked })}
        />
        <label className="form-check-label" htmlFor={id('enabled')}>
          Payment required
        </label>
      </div>

      <fieldset disabled={featureOff} className="border-0 p-0 m-0">
        <div className="row g-3">
          <div className="col-sm-6 col-lg-3">
            <label className="form-label" htmlFor={id('amount')}>
              {section.amountLabel}
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                id={id('amount')}
                className="form-control"
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value as unknown as number })}
              />
            </div>
            <div className="form-text">Excluding tax.</div>
          </div>

          <div className="col-sm-6 col-lg-2">
            <label className="form-label" htmlFor={id('tax')}>
              Tax %
            </label>
            <input
              id={id('tax')}
              className="form-control"
              type="number"
              min={0}
              max={100}
              step="0.01"
              inputMode="decimal"
              value={draft.taxPercentage}
              onChange={(e) => setDraft({ ...draft, taxPercentage: e.target.value as unknown as number })}
            />
            <div className="form-text">Payer owes {money(total(draft))}.</div>
          </div>

          <div className="col-sm-6 col-lg-3">
            <label className="form-label" htmlFor={id('period')}>
              {section.periodLabel}
            </label>
            <input id={id('period')} className="form-control" value={labelPeriod(config.billingPeriod)} readOnly disabled />
            <div className="form-text">{section.periodHelp}</div>
          </div>

          <div className="col-sm-6 col-lg-2">
            <label className="form-label" htmlFor={id('validity')}>
              {section.validityLabel}
            </label>
            <input
              id={id('validity')}
              className="form-control"
              type="number"
              min={1}
              max={3650}
              step={1}
              value={draft.listingValidityDays}
              onChange={(e) => setDraft({ ...draft, listingValidityDays: e.target.value as unknown as number })}
            />
            <div className="form-text">{section.validityHelp}</div>
          </div>

          <div className="col-sm-6 col-lg-2">
            <label className="form-label" htmlFor={id('grace')}>
              Grace period (days)
            </label>
            <input
              id={id('grace')}
              className="form-control"
              type="number"
              min={0}
              max={365}
              step={1}
              value={draft.gracePeriodDays}
              onChange={(e) => setDraft({ ...draft, gracePeriodDays: e.target.value as unknown as number })}
            />
            <div className="form-text">{section.graceHelp}</div>
          </div>

          <div className="col-sm-6 col-lg-2">
            <label className="form-label" htmlFor={id('currency')}>
              Currency
            </label>
            <select
              id={id('currency')}
              className="form-select"
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
            >
              <option value="INR">INR (₹)</option>
            </select>
            <div className="form-text">Only INR is supported.</div>
          </div>
        </div>
      </fieldset>

      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mt-3">
        <div className="small text-muted">
          <div>
            {section.countLabel} live on a paid record: <strong>{config.paidActiveCount}</strong> · live without one:{' '}
            <strong>{config.freeActivationCount}</strong>
          </div>
          <div>
            Plan <code>{config.managedPlanCode}</code>
            {config.updatedAtUtc && <> · last changed {dateTime(config.updatedAtUtc)}</>}
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={!changed || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

function badgeLabel(config: PaymentConfiguration, section: Section): string {
  if (section.canDisableFeature && !config.featureEnabled) return 'Switched off';
  return config.paymentEnabled ? 'Payment required' : 'Free';
}

function badgeTone(config: PaymentConfiguration, section: Section): string {
  if (section.canDisableFeature && !config.featureEnabled) return 'secondary';
  return config.paymentEnabled ? 'success' : 'warning';
}

function toDraft(config: PaymentConfiguration): PaymentConfigurationWrite {
  return {
    paymentEnabled: config.paymentEnabled,
    featureEnabled: config.featureEnabled,
    amount: config.amount,
    taxPercentage: config.taxPercentage,
    listingValidityDays: config.listingValidityDays,
    gracePeriodDays: config.gracePeriodDays,
    currency: config.currency,
  };
}

/** Preview only. The server recomputes the total it will actually charge. */
function total(draft: PaymentConfigurationWrite): number {
  const amount = Number(draft.amount) || 0;
  const tax = Number(draft.taxPercentage) || 0;
  return Math.round(amount * (1 + tax / 100) * 100) / 100;
}

function labelPeriod(period: PaymentConfiguration['billingPeriod']): string {
  switch (period) {
    case 'Monthly':
      return 'Monthly subscription';
    case 'Yearly':
      return 'Yearly, per organisation';
    default:
      return 'One-time per listing';
  }
}
